import { useMemo, useState } from 'react';

import { deleteProperty, unlockProperty } from '@vb-django/api/services/property';
import { usePropertyFetcher } from '@vb-django/components/minskog/hooks/usePropertyFetcher';
import { usePropertyRegeneration } from '@vb-django/components/minskog/hooks/usePropertyRegeneration';
import { useUpdateProperty } from '@vb-django/components/minskog/hooks/useUpdateProperty';

import { usePropsDataStore } from '@store/PropsDataStore';
import { useVisaValdaStore } from '@store/VisaValdaStore';

import { cn } from '@utils/cn';

import { Icon, Text } from '@ui-component-library';

import { coptureLayerList } from '../../../../../vb-django/components/minskog/helpers';
import { usePropertySelection } from '../../context/PropertySelectionContext';
import {
  canInteract,
  capitalizeAllWords,
  getPropertyStatus,
  removePropertyFromCookie
} from '../../PropertySelector.utils';

import { PopOverItem, PopoverMenu } from '../PopoverMenu';
import PropertyDetails from '../PropertyDetails/PropertyDetails';

import type { PropertyRowProps, PropertyRowStatus } from './PropertyRow.types';

const STATUS_TEXT: { [key in PropertyRowStatus]: string | null } = {
  loading: 'Läser in skogsbruksplan',
  broken: 'Kunde inte läsas in',
  locked: 'Låst',
  needsDownload: 'Behöver hämtas',
  hasUpdate: 'Uppdatering tillgänglig',
  active: null,
  inactive: null
};

const STATUS_STYLING: { [key in PropertyRowStatus]: string | undefined } = {
  loading: undefined,
  broken: 'border-l-[#CC9000]',
  locked: undefined,
  needsDownload: undefined,
  hasUpdate: undefined,
  active: 'border-l-[#A9A3F3] bg-[#FCFCFF]',
  inactive: undefined
};

const CTA_LABEL: { [key in PropertyRowStatus]?: string } = {
  locked: 'Lås upp',
  needsDownload: 'Hämta',
  broken: 'Försök igen',
  hasUpdate: 'Uppdatera'
};

const PropertyStatusIcon = ({ status }: { status: PropertyRowStatus }) => {
  switch (status) {
    case 'loading':
      return <Icon name="Loader" size="large" className="animate-spin px-4 py-3" />;
    case 'locked':
      return <Icon name="LockSimple" size="large" className="px-4 py-3" />;
    case 'needsDownload':
      return <Icon name="Download" size="large" className="px-4 py-3" />;
    case 'hasUpdate':
      return <Icon name="ClockCounterClockwise" size="large" className="px-4 py-3" />;
    case 'broken':
      return <Icon name="WarningFilled" size="large" className="px-4 py-3" />;
    case 'active':
      return (
        <div className="flex items-center justify-center px-4 py-3">
          <Icon
            name="CheckSimple"
            size="medium"
            className="h-5 w-5 rounded-xs bg-[#0F0F0F] text-[#fff]"
          />
        </div>
      );
    case 'inactive':
    default:
      return (
        <div className="flex items-center justify-center px-4 py-3">
          <div className="h-5 w-5 rounded-xs border border-solid border-[#E6E6E6] bg-[#FDFDFD]" />
        </div>
      );
  }
};

export const PropertyRow = ({ property, isActive = false }: PropertyRowProps) => {
  const { props: properties, setProps, setOneProp } = usePropsDataStore();
  const { setVisaValda } = useVisaValdaStore();
  const { fetchPropData } = usePropertyFetcher();
  const { updatePropertySections } = useUpdateProperty();
  const { regenerateProperty } = usePropertyRegeneration();
  const { toggleProperty, setAlertMessage } = usePropertySelection();
  const [isPropertyDetailsOpen, setIsPropertyDetailsOpen] = useState(false);
  const hasPlusAccess = useMemo(() => properties.some((p) => p.dfr), [properties]);

  const hasCoptureLayer = useMemo(
    () => coptureLayerList([property]).some((layer) => layer.ref === property.ref),
    [property]
  );

  const status = getPropertyStatus(property, isActive);
  const canToggle = canInteract(property);
  const showTags = canToggle;
  const isShared = property.role ? property.role !== 'owner' : Boolean(property.is_shared);
  const isLoading = status === 'loading';
  const statusText = STATUS_TEXT[status] ?? `${property.area} ha`;
  const ctaLabel = CTA_LABEL[status];
  const statusStyling = STATUS_STYLING[status];

  const handleSettingsClick = () => {
    setIsPropertyDetailsOpen(true);
  };

  const handleExportPDF = () => {
    window.open(`${window.location.origin}/map-api/sbp-to-pdf/?ref=${property.ref}`, '_blank');
  };

  const handleDelete = async () => {
    setAlertMessage(null);
    setOneProp({ ...property, loading: true });

    const response = await deleteProperty(property.ref, property.type);

    if (response.ok) {
      removePropertyFromCookie(property.ref);
      const updatedProps = properties.filter((p) => p.ref !== property.ref);
      setProps(updatedProps);

      if (property.active) {
        setVisaValda(true);
      }
    } else {
      setOneProp({ ...property, loading: false });
      setAlertMessage({
        title: 'Kunde inte radera fastighet',
        subtitle: 'Ett fel uppstod vid radering av fastigheten permanent.',
        designation: property.designation,
        helperInfo: 'Försök igen senare eller kontakta oss för rådgivning',
        isOpen: true,
        onClose: () => setAlertMessage(null)
      });
    }
  };

  const handleConfirmDelete = () => {
    setAlertMessage({
      title: 'Radera fastighet',
      subtitle: 'Är du säker på att du vill radera',
      designation: property.designation,
      isOpen: true,
      onPrimaryAction: () => handleDelete(),
      primaryActionText: 'Radera',
      onClose: () => setAlertMessage(null)
    });
  };

  const handleRetry = async () => {
    const result = await regenerateProperty(property, () => {});

    if (result?.error) {
      let subtitleError = 'Ett problem uppstod när vi försökte återskapa fastigheten.';
      switch (result.error) {
        case 'Could not find property/user combination or regenerated to soon.':
          subtitleError =
            'Det gick inte att återskapa fastigheten. Vänta en stund och försök igen. Om problemet kvarstår, kontakta oss.';
          break;
        case 'Property is deprecated':
          subtitleError =
            'Du har valt en gammal skogsbruksplan som inte går att återskapa. Du har en ny version av denna skogsbruksplan att välja istället som går att återskapa.';
          break;
        default:
          subtitleError = 'Ett problem uppstod när vi försökte återskapa fastigheten.';
      }

      setAlertMessage({
        title: 'Kunde inte återskapa fastighet',
        subtitle: subtitleError,
        helperInfo: 'Försök igen senare eller kontakta oss för rådgivning',
        isOpen: true,
        onClose: () => setAlertMessage(null)
      });
    }
  };

  const handleConfirmUnlock = () => {
    setAlertMessage({
      title: 'Lås upp fastighet',
      subtitle: 'Är du säker på att du vill låsa upp:',
      designation: property.designation,
      helperInfo: 'Du kan alltid låsa upp fastigheten senare om du vill.',
      primaryActionText: 'Lås upp',
      onPrimaryAction: async () => {
        setAlertMessage(null);
        setOneProp({ ...property, loading: true });

        try {
          const response = await unlockProperty(property.ref);
          if (response.ok) {
            setOneProp({ ...property, locked: false, loading: false });
          } else {
            if (response.error === 'exceeding_delete_limit') {
              setAlertMessage({
                title: 'Kunde inte låsa upp flera fastighet',
                subtitle: 'Vi ser att du är intresserad av att låsa upp flera fastigheter.',
                helperInfo: 'Du kan alltid låsa upp fastigheten senare om du vill.',
                isOpen: true,
                onClose: () => setAlertMessage(null)
              });
            }
            setOneProp({ ...property, loading: false });
          }
        } catch (error) {
          console.error(error);
          setOneProp({ ...property, loading: false });
        }
      },
      isOpen: true,
      onClose: () => setAlertMessage(null)
    });
  };

  const handleFetchData = () => {
    fetchPropData(setOneProp, property);
  };

  const handleUpdate = async () => {
    const sections = property?.data?.sections || [];
    const { data, error } = await updatePropertySections(sections);

    if (error) {
      setAlertMessage({
        title: 'Kunde inte uppdatera fastighet',
        subtitle: 'Ett problem uppstod när försökte uppdatera fastigheten.',
        helperInfo: 'Försök igen senare eller kontakta oss för rådgivning',
        isOpen: true,
        onClose: () => setAlertMessage(null)
      });
      return;
    }

    const updatedProps = properties.map((p) => {
      const update = data.find((i: { ref: string; updated_at: string }) => i.ref === p.ref);
      if (update) {
        return {
          ...p,
          data: update.new_data,
          updated_at: update.updated_at
        };
      }
      return p;
    });

    setProps(updatedProps);
  };

  const handleRowClick = () => {
    switch (status) {
      case 'locked':
        if (property.source === 'forestand' && !hasPlusAccess) {
          setAlertMessage({
            title: 'Du behöver ha minst en annan Fastighet med Plus för att låsa upp Forestand',
            isOpen: true,
            onClose: () => setAlertMessage(null)
          });
          return;
        }

        return handleConfirmUnlock();

      case 'needsDownload':
        return handleFetchData();

      case 'broken':
        return handleRetry();

      case 'hasUpdate':
        return handleUpdate();

      case 'active':
      case 'inactive':
        return toggleProperty(property.ref, !isActive);
      default:
        console.log('Default');
        return;
    }
  };

  return (
    <>
      <div
        className={cn(
          'flex flex-row items-center gap-1 border-b-1 border-l-2 border-solid border-b-[#E6E6E6]',
          !isLoading && 'cursor-pointer hover:bg-[#F5F5F5]',
          statusStyling ?? 'border-l-transparent bg-transparent'
        )}
      >
        <div onClick={handleRowClick} className="flex w-full flex-row items-center gap-1">
          <PropertyStatusIcon status={status} />

          <div className="flex min-w-0 flex-1 flex-col gap-1 py-3">
            <Text variant="text-sm/leading-normal/medium" className="capitalize">
              {property.designation.toLowerCase()}
            </Text>
            <div className="flex flex-row items-center justify-start gap-1">
              <Text
                variant="text-xs/leading-normal/medium"
                className={cn(status === 'broken' ? 'text-[#CC9000]' : 'text-[#737373]')}
              >
                {statusText}
              </Text>

              {showTags && isShared && (
                <>
                  <Icon name="DotFilled" className="fill-[#737373]" size="xxsmall" />
                  <Text
                    variant="text-xs/leading-normal/medium"
                    className="rounded-lg border border-solid border-transparent bg-[#0A0A0A]/5 p-1 text-[#737373]"
                  >
                    Delad
                  </Text>
                </>
              )}
              {showTags && property.dfr && (
                <>
                  <Icon name="DotFilled" className="fill-[#737373]" size="xxsmall" />
                  <Text
                    variant="text-xs/leading-normal/medium"
                    className="rounded-lg border border-solid border-transparent bg-[#F6F5FE] p-1 text-[#6856C3]"
                  >
                    Plus
                  </Text>
                </>
              )}
              {showTags && (property.type === 'SBO' || property.source === 'forestand') && (
                <>
                  <Icon name="DotFilled" className="fill-[#737373]" size="xxsmall" />
                  <div className="flex flex-row items-center gap-1 rounded-lg border border-solid border-transparent bg-[#F6F5FE] p-1">
                    <Icon name="UploadSimple" className="fill-[#737373]" size="small" />
                    <Text variant="text-xs/leading-normal/medium" className="text-[#1A1A1A]">
                      Forestand
                    </Text>
                  </div>
                </>
              )}
              {showTags && hasCoptureLayer && (
                <>
                  <Icon name="DotFilled" className="fill-[#737373]" size="xxsmall" />
                  <Text
                    variant="text-xs/leading-normal/medium"
                    className="rounded-lg border border-solid border-transparent bg-[#0A0A0A]/5 p-1 text-[#737373]"
                  >
                    Drönaranalys
                  </Text>
                </>
              )}
            </div>
          </div>
        </div>

        {ctaLabel && (
          /*
           Since the whole row is clickable, this is a visual-only CTA (no interactive states).
          */
          <div
            role="button"
            className="font-nista-grotesk text-charcoal-500 flex items-center justify-center gap-2 rounded-md border-1 border-solid border-stone-100 px-3 py-2 text-[14px] leading-4 font-medium tracking-[-0.3px] whitespace-nowrap"
            onClick={handleRowClick}
          >
            {status === 'hasUpdate' && (
              <Icon name="ClockCounterClockwise" size="large" className="px-4 py-3" />
            )}
            {ctaLabel}
          </div>
        )}

        <PopoverMenu
          icon="ThreeDotsSimple"
          className={cn('px-4 py-3', isLoading && 'pointer-events-none opacity-0')}
          disabled={isLoading}
          title={capitalizeAllWords(property.designation)}
          subtitle={`${property.area} ha`}
        >
          <div className="flex flex-col gap-1">
            <PopOverItem icon="GearSimple" label="Inställningar" onClick={handleSettingsClick} />
            {property.dfr && (
              <PopOverItem
                icon="PdfSimple"
                label="Exportera som PDF"
                onClick={handleExportPDF}
                isPlus={property.dfr}
              />
            )}
            {status === 'broken' && (
              <>
                <div className="h-px w-full bg-[#E6E6E6]/60" />
                <PopOverItem icon="ReloadSimple" label="Försök igen" onClick={handleRetry} />
              </>
            )}
            <div className="h-px w-full bg-[#E6E6E6]/60" />
            <PopOverItem
              icon="TrashBinSimple"
              label="Ta bort"
              onClick={handleConfirmDelete}
              variant="danger"
            />
          </div>
        </PopoverMenu>
      </div>

      <PropertyDetails
        property={property}
        isOpen={isPropertyDetailsOpen}
        onClose={() => setIsPropertyDetailsOpen(false)}
      />
    </>
  );
};
